import React, { useState, useMemo, useEffect, useRef } from "react";
import { Flex, Text, SegmentedControl, Card, Grid, Button, Box, TextField, Select, AlertDialog, IconButton, Tooltip, DropdownMenu,
  Dialog, Callout, Badge, Separator, Progress, Checkbox } from "@radix-ui/themes";
import { EnvelopeClosedIcon, ChatBubbleIcon, MobileIcon, PlusIcon, TrashIcon, ExclamationTriangleIcon, Pencil2Icon, GridIcon, ArrowLeftIcon, CopyIcon } from "@radix-ui/react-icons";
import { useProfileContext } from "context";
import { useGetWhatsappCredentialAccounts, useMarketingEmailTemplates, useMarketingWhatsappTemplates } from "hooks";
import { useLocation, useNavigate } from "react-router-dom";
import { getEmailTemplateDetails } from "api/bMarketingApi";
import MarketingWhatsappTemplatesPanel from "./MarketingWhatsappTemplatesPanel";
import MarketingWhatsappPreview from "./MarketingWhatsappPreview";
import {
  getWhatsappTemplateStatusLabel,
  WHATSAPP_TEMPLATE_LANGUAGES,
  WHATSAPP_TEMPLATE_STATUS_COLORS,
} from "./marketingWhatsappStore";

// Import templates for preview generation
import ElegantClassicTemplate from './templates/ElegantClassicEmailTemplate';
import ThankYouReviewTemplate from './templates/ThankYouReviewTemplate';
import HospitalityPromoTemplate from './templates/HospitalityPromoTemplate';

// Template mapping for preview generation
const TEMPLATES = {
  'elegant-classic': ElegantClassicTemplate,
  'thank-you-review': ThankYouReviewTemplate,
  'hospitality-promo': HospitalityPromoTemplate,
};

// Default template for each purpose
const PURPOSE_TEMPLATE_MAP = {
  welcome: 'elegant-classic',
  review: 'thank-you-review',
  promotion: 'hospitality-promo',
  newsletter: 'elegant-classic',
};

// Purpose badge config
const PURPOSE_BADGE = {
  welcome: { label: 'Welcome email', color: 'crimson' },
  review: { label: 'Review email', color: 'purple' },
  promotion: { label: 'Promotional email', color: 'orange' },
  newsletter: { label: 'Newsletter', color: 'blue' },
};

const WHATSAPP_TEMPLATE_INTEGRATION_REQUIRED_MESSAGE = "Connect a WhatsApp integration before creating WhatsApp templates.";

// Template Preview Component
const TemplatePreview = ({ template }) => {
  // Check if this is an imported template
  const isImported = template.template_source && template.template_source !== 'internal';
  
  const previewHtml = useMemo(() => {
    // For scratch templates, use the html_content directly (or show empty state)
    if (template.template_type === 'scratch') {
      if (template.html_content) return template.html_content;
      // No html_content yet — render the same empty-state the designer shows
      const orgName = template.name || 'Your Brand';
      return `<div style="background:#F3F4F6;padding:24px 20px;font-family:Arial,sans-serif;">
        <div style="max-width:600px;margin:0 auto;background:#FFFFFF;border-radius:10px;overflow:hidden;box-shadow:0 1px 8px rgba(0,0,0,0.07);">
          <div style="padding:80px 30px;text-align:center;">
            <div style="font-size:48px;margin-bottom:12px;opacity:0.25;">✉️</div>
            <div style="font-size:16px;font-weight:700;margin-bottom:6px;color:#111827;">Start building your email</div>
            <div style="font-size:14px;color:#9CA3AF;">Drag blocks from the left panel, or click them to add.</div>
          </div>
          <div style="border-top:1px solid #e5e7eb;background:#f9fafb;padding:28px 30px 24px;text-align:center;">
            <div style="font-size:13px;color:#9CA3AF;">&copy; ${new Date().getFullYear()} ${orgName}. All rights reserved.</div>
            <div style="font-size:12px;color:#9CA3AF;margin-top:8px;"><a href="#" style="color:#9CA3AF;text-decoration:underline;">update your preferences</a> or <a href="#" style="color:#9CA3AF;text-decoration:underline;">unsubscribe</a></div>
          </div>
        </div>
      </div>`;
    }

    // For imported templates, use the html_content directly
    if (isImported && template.html_content) {
      return template.html_content;
    }
    
    // For internal templates, generate preview from design_json
    const designJson = template.design_json || {};
    const templateId = designJson.templateId || PURPOSE_TEMPLATE_MAP[template.purpose] || 'elegant-classic';
    const templateModule = TEMPLATES[templateId];
    
    if (!templateModule) return null;
    
    // Get default values and merge with saved design
    const formData = {
      ...templateModule.getDefaultValues(template.purpose),
      ...designJson,
    };
    
    return templateModule.generatePreviewHtml(formData);
  }, [template, isImported]);

  const badgeConfig = PURPOSE_BADGE[template.purpose] || { label: template.purpose, color: 'gray' };

  if (!previewHtml) return null;

  return (
    <Box style={{ width: '100%', height: '100%', overflow: 'hidden',
      borderTopLeftRadius: 'var(--radius-2)', borderTopRightRadius: 'var(--radius-2)'}}>
      <Box style={{ transform: 'scale(0.25)', transformOrigin: 'top left', width: '400%', height: '400%', pointerEvents: 'none' }}>
        <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
      </Box>
      {/* Purpose Badge */}
      <Badge color={badgeConfig.color} variant="solid" size="1" style={{ position: 'absolute', top: 8, left: 8,}}>
        {badgeConfig.label}
      </Badge>
    </Box>
  );
};

export default function MarketingTemplates() {
  const { userProfile } = useProfileContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [whatsappTemplateToDelete, setWhatsappTemplateToDelete] = useState(null);
  const [associatedAutomations, setAssociatedAutomations] = useState([]);
  const [isLoadingAutomations, setIsLoadingAutomations] = useState(false);
  const { emailTemplates, isLoading, deleteTemplate, isDeleting, createTemplateAsync, isCreating, importTemplateAsync, isImporting, duplicateTemplateAsync, isDuplicating } = useMarketingEmailTemplates(userProfile.organisation_id);
  const {
    whatsappTemplates,
    isLoading: isLoadingWhatsappTemplates,
    createTemplateAsync: createWhatsappTemplateAsync,
    isCreating: isCreatingWhatsappTemplate,
    deleteTemplateAsync: deleteWhatsappTemplateAsync,
    isDeleting: isDeletingWhatsappTemplate,
  } = useMarketingWhatsappTemplates(userProfile.organisation_id);
  const { data: whatsappAccountsData } = useGetWhatsappCredentialAccounts(userProfile?.organisation_id);
  const whatsappAccounts = useMemo(() => whatsappAccountsData?.data?.accounts || [], [whatsappAccountsData]);
  const hasWhatsappIntegration = whatsappAccounts.length > 0;
  const [selectedSegment, setSelectedSegment] = useState("all");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isWhatsappCreateDialogOpen, setIsWhatsappCreateDialogOpen] = useState(false);
  const [newWhatsappTemplateName, setNewWhatsappTemplateName] = useState("");
  const [selectedWhatsappLanguage, setSelectedWhatsappLanguage] = useState("");
  const [selectedWhatsappConfigId, setSelectedWhatsappConfigId] = useState("");

  // Duplicate template modal state
  const [templateToDuplicate, setTemplateToDuplicate] = useState(null);
  const [duplicateForm, setDuplicateForm] = useState({ name: "", subject: "", purpose: "welcome" });

  // Design choice modal state
  const [isDesignChoiceOpen, setIsDesignChoiceOpen] = useState(false);
  const [pendingTemplateData, setPendingTemplateData] = useState(null);
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  
  // Import mode state
  const [isImportMode, setIsImportMode] = useState(false);
  const [importSource, setImportSource] = useState("canva");
  const [importFile, setImportFile] = useState(null);
  const [importFileName, setImportFileName] = useState("");
  const fileInputRef = useRef(null);

  // Fetch associated automations when template is selected for deletion
  useEffect(() => {
    const requestedSegment = location.state?.selectedSegment;
    if (["all", "email", "sms", "whatsapp"].includes(requestedSegment)) {
      setSelectedSegment(requestedSegment);
    }
  }, [location.state]);

  useEffect(() => {
    if (templateToDelete?.template_id) {
      setIsLoadingAutomations(true);
      getEmailTemplateDetails(templateToDelete.template_id)
        .then((res) => {
          setAssociatedAutomations(res.data.associated_automations || []);
        })
        .catch((err) => {
          console.error('Failed to fetch template details:', err);
          setAssociatedAutomations([]);
        })
        .finally(() => {
          setIsLoadingAutomations(false);
        });
    } else {
      setAssociatedAutomations([]);
    }
  }, [templateToDelete?.template_id]);

  const handleDeleteTemplate = () => {
    if (!templateToDelete) return;
    deleteTemplate(templateToDelete.template_id, {
      onSuccess: () => {
        setTemplateToDelete(null);
        setAssociatedAutomations([]);
      }
    });
  };

  const handleDeleteWhatsappTemplate = async () => {
    if (!whatsappTemplateToDelete) return;
    try {
      await deleteWhatsappTemplateAsync(whatsappTemplateToDelete.id);
      setWhatsappTemplateToDelete(null);
    } catch (error) {
      console.error("Failed to delete WhatsApp template:", error);
      alert("Failed to delete WhatsApp template. Please try again.");
    }
  };

  const handleDeleteClick = (e, template) => {
    e.stopPropagation(); // Prevent opening editor
    setTemplateToDelete(template);
  };

  const [newTemplate, setNewTemplate] = useState({ name: "", subject: "", purpose: "welcome" });

  // Reset import state when dialog closes
  useEffect(() => {
    if (!isCreateDialogOpen) {
      setIsImportMode(false);
      setImportSource("canva");
      setImportFile(null);
      setImportFileName("");
      setNewTemplate({ name: "", subject: "", purpose: "welcome" });
    }
  }, [isCreateDialogOpen]);

  useEffect(() => {
    if (isWhatsappCreateDialogOpen && !selectedWhatsappConfigId && whatsappAccounts.length === 1) {
      setSelectedWhatsappConfigId(String(whatsappAccounts[0].id));
    }
  }, [isWhatsappCreateDialogOpen, selectedWhatsappConfigId, whatsappAccounts]);

  useEffect(() => {
    if (!isWhatsappCreateDialogOpen) {
      setNewWhatsappTemplateName("");
      setSelectedWhatsappLanguage("");
      setSelectedWhatsappConfigId("");
    }
  }, [isWhatsappCreateDialogOpen]);

  // Handle file upload
  const handleFileUpload = (event) => {
    const file = event.target?.files?.[0];
    if (file) {
      // Validate file type (ZIP only)
      if (!file.name.endsWith('.zip')) {
        alert('Please upload a ZIP file');
        return;
      }
      
      // Validate file size (max 50MB)
      const maxSizeInBytes = 50 * 1024 * 1024;
      if (file.size > maxSizeInBytes) {
        alert('File size must be less than 50MB');
        return;
      }
      
      setImportFile(file);
      setImportFileName(file.name);
    }
  };

  const handleFileClear = () => {
    setImportFile(null);
    setImportFileName("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleCreateTemplate = async () => {
    if (!newTemplate.name || !newTemplate.purpose || !newTemplate.subject) {
      return;
    }

    // Import mode - use import API
    if (isImportMode) {
      if (!importFile) {
        alert('Please upload a ZIP file');
        return;
      }

      try {
        const formData = new FormData();
        formData.append('source', importSource);
        formData.append('name', newTemplate.name);
        formData.append('purpose', newTemplate.purpose);
        formData.append('subject', newTemplate.subject);
        formData.append('organisation', userProfile.organisation_id);
        formData.append('template_file', importFile);

        const response = await importTemplateAsync(formData);
        
        setIsCreateDialogOpen(false);
        setNewTemplate({ name: "", subject: "", purpose: "welcome" });
        handleFileClear();

        // Show success with warnings if any
        if (response.data.warnings && response.data.warnings.length > 0) {
          console.warn('Import warnings:', response.data.warnings);
        }

        // Navigate to view the imported template (read-only)
        navigate(`/marketing/templates/editor/${response.data.template_id}`, {
          state: { template: response.data }
        });
      } catch (error) {
        console.error('Failed to import template:', error);
        alert(error.response?.data?.error || 'Failed to import template');
      }
    } else {
      // Regular mode — defer backend creation until user picks design mode
      setPendingTemplateData({ ...newTemplate });
      setIsCreateDialogOpen(false);
      setIsDesignChoiceOpen(true);
    }
  };

  const handleCreateWhatsappTemplate = async () => {
    const cleanedName = newWhatsappTemplateName.trim();
    if (!cleanedName || !selectedWhatsappConfigId || !selectedWhatsappLanguage) {
      return;
    }

    try {
      const template = await createWhatsappTemplateAsync({
        organisation: userProfile.organisation_id,
        whatsapp_config: selectedWhatsappConfigId,
        name: cleanedName,
        language: selectedWhatsappLanguage,
        header_enabled: true,
        header_type: "text",
        header_text: cleanedName,
        body: "",
        footer_enabled: false,
        footer_text: "",
        buttons: [],
      });
      setIsWhatsappCreateDialogOpen(false);
      navigate(`/marketing/templates/whatsapp/${template.id}`, { state: { template } });
    } catch (error) {
      alert("Unable to create the WhatsApp template right now");
    }
  };

  const handleDesignChoiceBack = () => {
    setIsDesignChoiceOpen(false);
    setIsCreateDialogOpen(true);
  };

  const handleDesignChoice = async (mode) => {
    if (!pendingTemplateData) return;
    setIsCreatingTemplate(true);
    try {
      const templateData = {
        name: pendingTemplateData.name,
        subject: pendingTemplateData.subject,
        purpose: pendingTemplateData.purpose,
        organisation: userProfile.organisation_id,
        mjml_content: '',
        template_type: mode === 'scratch' ? 'scratch' : 'predefined',
        design_json: {
          templateId: PURPOSE_TEMPLATE_MAP[pendingTemplateData.purpose] || 'elegant-classic',
        },
      };

      const response = await createTemplateAsync(templateData);
      const createdTemplate = response.data;

      setIsDesignChoiceOpen(false);
      setPendingTemplateData(null);
      setNewTemplate({ name: "", subject: "", purpose: "welcome" });

      if (mode === 'scratch') {
        navigate(`/marketing/templates/designer/${createdTemplate.template_id}`, {
          state: { template: createdTemplate },
        });
      } else {
        navigate(`/marketing/templates/editor/${createdTemplate.template_id}`, {
          state: { template: createdTemplate, designMode: mode },
        });
      }
    } catch (error) {
      console.error('Failed to create template:', error);
      alert('Failed to create template. Please try again.');
    } finally {
      setIsCreatingTemplate(false);
    }
  };

  // Open visual editor for existing template
  const handleOpenVisualEditor = (template) => {
    const editorPath = template.template_type === 'scratch'
      ? `/marketing/templates/designer/${template.template_id}`
      : `/marketing/templates/editor/${template.template_id}`;
    navigate(editorPath, {
      state: { template }
    });
  };

  // Open the duplicate dialog, prefilling from the original template
  const handleDuplicateClick = (e, template) => {
    e.stopPropagation(); // Prevent opening editor
    setTemplateToDuplicate(template);
    setDuplicateForm({
      name: `Copy of ${template.name}`,
      subject: template.subject || "",
      purpose: template.purpose || "welcome",
    });
  };

  // Confirm duplication: clone on the backend, then navigate to the matching editor
  const handleConfirmDuplicate = async () => {
    if (!templateToDuplicate || !duplicateForm.name.trim()) return;
    try {
      const response = await duplicateTemplateAsync({
        templateId: templateToDuplicate.template_id,
        data: {
          name: duplicateForm.name.trim(),
          subject: duplicateForm.subject.trim(),
          purpose: duplicateForm.purpose,
        },
      });
      const createdTemplate = response.data;
      setTemplateToDuplicate(null);
      handleOpenVisualEditor(createdTemplate);
    } catch (error) {
      console.error('Failed to duplicate template:', error);
      alert(error.response?.data?.error || 'Failed to duplicate template. Please try again.');
    }
  };

  const filteredTemplates = useMemo(() => {
    if (selectedSegment === "sms" || selectedSegment === "whatsapp") return [];

    const emailItems = (emailTemplates || []).map((template) => ({
      channel: "email",
      sortDate: template.created_at || template.updated_at || "",
      template,
    }));

    const whatsappItems = selectedSegment === "all"
      ? (whatsappTemplates || []).map((template) => ({
          channel: "whatsapp",
          sortDate: template.created_at || template.updated_at || "",
          template,
        }))
      : [];

    return [...emailItems, ...whatsappItems];
  }, [selectedSegment, emailTemplates, whatsappTemplates]);

  // Sort templates by created_at
  const sortedTemplates = useMemo(() => {
    if (!filteredTemplates || filteredTemplates.length === 0) return [];
    return [...filteredTemplates].sort((a, b) => new Date(a.sortDate || 0) - new Date(b.sortDate || 0));
  }, [filteredTemplates]);

  const hasTemplates = sortedTemplates.length > 0;

  return (
    <>
      {/* Loading Progress Bar */}
      {(isLoading || (selectedSegment === "all" && isLoadingWhatsappTemplates)) && (
        <div style={{ 
          position: "fixed", 
          top: 0, 
          left: 0, 
          width: "100%", 
          zIndex: 1000 
        }}>
          <Progress size="1" duration="1" />
        </div>
      )}
      
      <Flex direction="column" gap="5" style={{ alignSelf: "stretch", alignItems: "center", flex: "1 0 0" }}>
        {selectedSegment === "whatsapp" ? (
          <MarketingWhatsappTemplatesPanel
            selectedSegment={selectedSegment}
            onSelectedSegmentChange={setSelectedSegment}
          />
        ) : (
        <>
        {/* Header */}
        <Flex justify="between" align="center" style={{ alignSelf: "stretch" }}>
          {/* Filter Segmented Control */}
          <SegmentedControl.Root value={selectedSegment} onValueChange={setSelectedSegment}>
            <SegmentedControl.Item value="all">All</SegmentedControl.Item>
            <SegmentedControl.Item value="email">
              <Flex gap="1" align="center">
                <EnvelopeClosedIcon />
                <Text>Email</Text>
              </Flex>
            </SegmentedControl.Item>
            <SegmentedControl.Item value="sms">
              <Flex gap="1" align="center">
                <MobileIcon />
                <Text>SMS</Text>
              </Flex>
            </SegmentedControl.Item>
            <SegmentedControl.Item value="whatsapp">
              <Flex gap="1" align="center">
                <ChatBubbleIcon />
                <Text>Whatsapp</Text>
              </Flex>
            </SegmentedControl.Item>
          </SegmentedControl.Root>
          {selectedSegment === "all" ? (
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <Button variant="soft">
                  <PlusIcon width="16" height="16" />
                  Create template
                </Button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content>
                <DropdownMenu.Item onClick={() => setIsCreateDialogOpen(true)}>
                  <Flex gap="2" align="center">
                    <EnvelopeClosedIcon />
                    <Text>Email template</Text>
                  </Flex>
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  onSelect={(event) => {
                    if (!hasWhatsappIntegration) {
                      event.preventDefault();
                      return;
                    }

                    setIsWhatsappCreateDialogOpen(true);
                  }}
                  style={!hasWhatsappIntegration ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                >
                  <Tooltip content={hasWhatsappIntegration ? "Create WhatsApp template" : WHATSAPP_TEMPLATE_INTEGRATION_REQUIRED_MESSAGE}>
                  <Flex gap="2" align="center">
                    <ChatBubbleIcon />
                    <Text>WhatsApp template</Text>
                  </Flex>
                  </Tooltip>
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Root>
          ) : (
            <Button variant="soft" onClick={() => setIsCreateDialogOpen(true)}
              disabled={selectedSegment === "sms" || selectedSegment === "whatsapp" }>
              <PlusIcon width="16" height="16" />
              Create template
            </Button>
          )}
        </Flex>
        {/* Template cards */}
        { selectedSegment === "sms" ? (
          <Flex direction="column" style={{justifyContent: 'center', alignItems: 'center', alignSelf: 'stretch',
            border: '1px dashed var(--gray-a6)', borderRadius: 'var(--radius-4)', height: '100%'}}>
              <Text color="gray">SMS features coming soon.</Text>
            </Flex>
          ) : (
        <Flex direction="column" gap="5" style={{ alignSelf: "stretch", width: "100%", overflow: 'auto', height: '100%',
          flex: "1 0 0" }}>
          { !hasTemplates ? (
            <Flex direction="column" style={{justifyContent: 'center', alignItems: 'center', alignSelf: 'stretch',
              border: '1px dashed var(--gray-a6)', borderRadius: 'var(--radius-4)', height: '100%', padding: 'var(--space-6)'}}>
              <Text color="gray">No templates available. Create your first template to get started.</Text>
            </Flex>
          ) : (
            <Grid columns="3" gap="4" style={{ alignSelf: "stretch", width: "100%" }}>
              {sortedTemplates.map(({ channel, template }) => {
                const reviewStatus = template.reviewStatus || "draft";
                const statusLabel = getWhatsappTemplateStatusLabel(reviewStatus, template.qualityScore);

                return (
                  <Card asChild
                    key={`${channel}-${template.id}`}
                    onClick={() => {
                      if (channel === "whatsapp") {
                        navigate(`/marketing/templates/whatsapp/${template.id}`, { state: { template } });
                        return;
                      }
                      handleOpenVisualEditor(template);
                    }}
                    style={{ display: "flex", flexDirection: "column", padding: 0, overflow: 'hidden', height: '200px' }}>
                    <button>
                      <Flex direction="column" style={{ width: "100%", height: "100%" }}>
                      {/* Template Preview with Badge */}
                      {channel === "whatsapp" ? (
                        <Box style={{ position: "relative", width: "100%", height: "100%", overflow: "hidden", background: "#e6ddd4" }}>
                          <MarketingWhatsappPreview template={template} compact />
                        </Box>
                      ) : (
                        <TemplatePreview template={template} />
                      )}
                      <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />
                      {/* Template Info */}
                      <Flex direction="column" gap="1" p="4">
                        <Flex justify="between" align="start" gap="3">
                          <Flex direction="column" gap="2" style={{ minWidth: 0 }}>
                            <Text weight="medium" size="2" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
                              {template.name}
                            </Text>
                            {channel === "whatsapp" && statusLabel ? (
                              <Badge color={WHATSAPP_TEMPLATE_STATUS_COLORS[reviewStatus] || "gray"} variant="soft" style={{ alignSelf: "flex-start" }}>
                                {statusLabel}
                              </Badge>
                            ) : null}
                          </Flex>
                          <Flex align="center" gap="2">
                            {channel === "email" ? (
                              <Tooltip content="Duplicate template">
                                <IconButton
                                  size="1"
                                  variant="soft"
                                  color="blue"
                                  onClick={(e) => handleDuplicateClick(e, template)}
                                >
                                  <CopyIcon />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                            <Tooltip content="Delete template">
                              <IconButton 
                                size="1" 
                                variant="soft" 
                                color="red"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (channel === "whatsapp") {
                                    setWhatsappTemplateToDelete(template);
                                    return;
                                  }
                                  handleDeleteClick(e, template);
                                }}
                              >
                                <TrashIcon />
                              </IconButton>
                            </Tooltip>
                          </Flex>
                        </Flex>
                        {channel === "whatsapp" ? (
                          null
                        ) : (
                          <Flex justify="between" align="center" gap="1">
                            <Text size="1" color="gray" style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {template.subject}
                            </Text>
                            {template.template_source && template.template_source !== 'internal' && (
                              <Badge color="amber" variant="outline" size="1">Imported template</Badge>
                            )}
                          </Flex>
                        )}
                      </Flex>
                    </Flex>
                  </button>
                </Card>
              );
            })}
            </Grid>
          )}
        </Flex>
        )}
        </>
        )}

        {/* Create Dialog */}
        <Dialog.Root open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <Dialog.Content style={{ maxWidth: 550 }}>
            <Dialog.Title>{isImportMode ? 'Import Email Template' : 'Create Email Template'}</Dialog.Title>
            <Dialog.Description size="2" color="gray" mb="4">
              {isImportMode 
                ? 'Import an external email template to use in your marketing campaigns and automations.'
                : 'Create a new email template to use in your marketing campaigns and automations.'
              }
            </Dialog.Description>
            <Flex direction="column" gap="3">
              {/* Name input field */}
              <Flex direction="column" gap="1">
                <Text size="2" weight="bold">Name</Text>
                <TextField.Root 
                  placeholder="Template Name" 
                  value={newTemplate.name} 
                  onChange={(e) => setNewTemplate({...newTemplate, name: e.target.value})} 
                />
              </Flex>
              {/* Purpose input field */}
              <Flex direction="column" gap="1">
                <Text size="2" weight="bold">Purpose</Text>
                <Select.Root value={newTemplate.purpose} onValueChange={(val) => setNewTemplate({...newTemplate, purpose: val})}>
                  <Select.Trigger style={{ width: '100%' }} />
                  <Select.Content>
                    <Select.Item value="welcome">Welcome email</Select.Item>
                    <Select.Item value="newsletter">Newsletter</Select.Item>
                    <Select.Item value="review">Review request email</Select.Item>
                    <Select.Item value="promotion" disabled>
                      <Flex gap="1" align="center">
                        Promotional email
                        <Badge size="1" color="gray" radius="full">Coming soon</Badge>
                      </Flex>
                    </Select.Item>
                  </Select.Content>
                </Select.Root>
              </Flex>
              {/* Subject input field */}
              <Flex direction="column" gap="1">
                <Text size="2" weight="bold">Subject</Text>
                <TextField.Root
                  placeholder="Email's subject line" 
                  value={newTemplate.subject} 
                  onChange={(e) => setNewTemplate({...newTemplate, subject: e.target.value})} 
                />
              </Flex>

              {/* Import Mode Toggle */}
              <Flex direction="column" gap="1">
                <Flex align="center" gap="2">
                  <Checkbox
                    id="import-mode"
                    checked={isImportMode}
                    onCheckedChange={setIsImportMode}
                  />
                  <Text as="label" htmlFor="import-mode" size="2" weight="medium">
                    Import template
                  </Text>
                </Flex>
                <Text size="1" color="gray">
                  Import a template by uploading a ZIP file exported from an external design tool.
                </Text>
              </Flex>

              {/* Source selector - only show in import mode */}
              {isImportMode && (
                <Flex direction="column" gap="1">
                  <Text size="2" weight="bold">Design tool</Text>
                  <Select.Root value={importSource} onValueChange={setImportSource}>
                    <Select.Trigger style={{ width: '100%' }} />
                    <Select.Content>
                      <Select.Item value="canva">Canva</Select.Item>
                      <Select.Item value="mailchimp" disabled>
                        <Flex gap="1" align="center">
                          Mailchimp
                          <Badge size="1" color="gray" radius="full">Coming soon</Badge>
                        </Flex>
                      </Select.Item>
                      <Select.Item value="beefree" disabled>
                        <Flex gap="1" align="center">
                          Beefree
                          <Badge size="1" color="gray" radius="full">Coming soon</Badge>
                        </Flex>
                      </Select.Item>
                    </Select.Content>
                  </Select.Root>
                </Flex>
              )}

              {/* File Upload - only show in import mode */}
              {isImportMode && (
                <Flex direction="column" gap="1">
                  <Text size="2" weight="bold">Template file (zip)</Text>
                  <Flex gap="3" align="center">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".zip"
                      onChange={handleFileUpload}
                      style={{ display: 'none' }}
                    />
                    <Button
                      variant="soft"
                      onClick={() => fileInputRef.current?.click()}
                      style={{ flex: 1 }}
                    >
                      {importFileName || 'Choose file'}
                    </Button>
                    {importFile && (
                      <IconButton
                        variant="soft"
                        color="red"
                        onClick={handleFileClear}
                      >
                        <TrashIcon />
                      </IconButton>
                    )}
                  </Flex>
                  {importFile && (
                    <Text size="1" color="gray">
                      File size: {((importFile.size / 1024)/ 1024).toFixed(1)} MB
                    </Text>
                  )}
                </Flex>
              )}
            </Flex>
            <Flex gap="3" mt="5" justify="end">
              <Dialog.Close>
                <Button variant="soft" color="gray">Cancel</Button>
              </Dialog.Close>
              <Button 
                onClick={handleCreateTemplate} 
                disabled={
                  !newTemplate.name || 
                  !newTemplate.subject || 
                  (isImportMode && !importFile) ||
                  isCreating || 
                  isImporting
                }
                loading={isCreating || isImporting}
              >
                {isImportMode ? 'Import' : 'Create'}
              </Button>
            </Flex>
          </Dialog.Content>
        </Dialog.Root>

        <Dialog.Root open={isWhatsappCreateDialogOpen} onOpenChange={setIsWhatsappCreateDialogOpen}>
          <Dialog.Content style={{ maxWidth: 520 }}>
            <Dialog.Title>Create WhatsApp Template</Dialog.Title>
            <Dialog.Description size="2" color="gray">
              Name the template first, then continue in the WhatsApp editor to build the message.
            </Dialog.Description>

            <Flex direction="column" gap="3" mt="4">
              <Flex direction="column" gap="1">
                <Text size="2" weight="medium">Name</Text>
                <TextField.Root
                  placeholder="Weekend offer"
                  value={newWhatsappTemplateName}
                  onChange={(event) => setNewWhatsappTemplateName(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      handleCreateWhatsappTemplate();
                    }
                  }}
                />
              </Flex>
              <Flex direction="column" gap="1">
                <Text size="2" weight="medium">WhatsApp account</Text>
                <Select.Root value={selectedWhatsappConfigId} onValueChange={setSelectedWhatsappConfigId} disabled={whatsappAccounts.length === 0}>
                  <Select.Trigger placeholder="Select account" style={{ width: "100%" }} />
                  <Select.Content position="popper">
                    {whatsappAccounts.map((account) => (
                      <Select.Item key={account.id} value={String(account.id)}>
                        {account.account_name || `WhatsApp Business Account ${account.business_id}`}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              </Flex>
              <Flex direction="column" gap="1">
                <Text size="2" weight="medium">Language</Text>
                <Select.Root value={selectedWhatsappLanguage} onValueChange={setSelectedWhatsappLanguage}>
                  <Select.Trigger placeholder="Select language" style={{ width: "100%" }} />
                  <Select.Content position="popper">
                    {WHATSAPP_TEMPLATE_LANGUAGES.map((language) => (
                      <Select.Item key={language.value} value={language.value}>
                        {language.label}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              </Flex>
            </Flex>

            <Flex gap="3" mt="4" justify="end">
              <Dialog.Close>
                <Button variant="soft" color="gray">Cancel</Button>
              </Dialog.Close>
              <Button
                onClick={handleCreateWhatsappTemplate}
                disabled={!newWhatsappTemplateName.trim() || !selectedWhatsappConfigId || !selectedWhatsappLanguage || isCreatingWhatsappTemplate}
                loading={isCreatingWhatsappTemplate}
              >
                Create template
              </Button>
            </Flex>
          </Dialog.Content>
        </Dialog.Root>

        {/* Design Choice Modal */}
        <Dialog.Root
          open={isDesignChoiceOpen}
          onOpenChange={(open) => {
            if (!open && !isCreatingTemplate) {
              setIsDesignChoiceOpen(false);
              setPendingTemplateData(null);
            }
          }}
        >
          <Dialog.Content style={{ maxWidth: 600 }}>
            <Dialog.Title>How would you like to design your template?</Dialog.Title>
            <Dialog.Description size="2" color="gray" mb="5">
              Choose how you want to design{pendingTemplateData?.name ? ` "${pendingTemplateData.name}"` : ' your template'}.
            </Dialog.Description>

            <Flex gap="4" direction="row">
              {/* Predefined templates card */}
              <button
                disabled={isCreatingTemplate}
                onClick={() => handleDesignChoice('predefined')}
                style={{
                  all: 'unset',
                  boxSizing: 'border-box',
                  flex: 1,
                  cursor: isCreatingTemplate ? 'not-allowed' : 'pointer',
                  border: '2px solid var(--accent-a6)',
                  borderRadius: 'var(--radius-3)',
                  padding: 'var(--space-5)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  textAlign: 'center',
                  background: 'var(--accent-a2)',
                  opacity: isCreatingTemplate ? 0.6 : 1,
                }}
              >
                <Box
                  style={{
                    width: 56,
                    height: 56,
                    flexShrink: 0,
                    borderRadius: 'var(--radius-3)',
                    background: 'var(--accent-a4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <GridIcon width={28} height={28} style={{ color: 'var(--accent-9)' }} />
                </Box>
                <Text weight="bold" size="3">Predefined templates</Text>
                <Text size="2" color="gray">
                  Use our professionally designed template and customise it to match your brand.
                </Text>
              </button>

              {/* Design from scratch card */}
              <button
                disabled={isCreatingTemplate}
                onClick={() => handleDesignChoice('scratch')}
                style={{
                  all: 'unset',
                  boxSizing: 'border-box',
                  flex: 1,
                  cursor: isCreatingTemplate ? 'not-allowed' : 'pointer',
                  border: '2px solid var(--gray-a6)',
                  borderRadius: 'var(--radius-3)',
                  padding: 'var(--space-5)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  textAlign: 'center',
                  background: 'var(--gray-a2)',
                  opacity: isCreatingTemplate ? 0.6 : 1,
                }}
              >
                <Box
                  style={{
                    width: 56,
                    height: 56,
                    flexShrink: 0,
                    borderRadius: 'var(--radius-3)',
                    background: 'var(--gray-a4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Pencil2Icon width={28} height={28} style={{ color: 'var(--gray-9)' }} />
                </Box>
                <Text weight="bold" size="3">Design from scratch</Text>
                <Text size="2" color="gray">
                  Build your own unique layout using our drag-and-drop email designer.
                </Text>
              </button>
            </Flex>

            <Flex gap="3" mt="5" justify="between" align="center">
              <Button
                variant="ghost"
                color="gray"
                onClick={handleDesignChoiceBack}
                disabled={isCreatingTemplate}
              >
                <ArrowLeftIcon />
                Back
              </Button>
              <Dialog.Close>
                <Button variant="soft" color="gray" disabled={isCreatingTemplate}>
                  Cancel
                </Button>
              </Dialog.Close>
            </Flex>
          </Dialog.Content>
        </Dialog.Root>

        {/* Duplicate Template Dialog */}
        <Dialog.Root open={!!templateToDuplicate} onOpenChange={(open) => { if (!open && !isDuplicating) setTemplateToDuplicate(null); }}>
          <Dialog.Content style={{ maxWidth: 500 }}>
            <Dialog.Title>Copy email template</Dialog.Title>
            <Dialog.Description size="2" color="gray" mb="4">
              Create a duplicate of "{templateToDuplicate?.name}". The copy starts as an exact clone that you can edit independently.
            </Dialog.Description>
            <Flex direction="column" gap="3">
              {/* Name input field */}
              <Flex direction="column" gap="1">
                <Text size="2" weight="bold">Name</Text>
                <TextField.Root 
                  placeholder="Template Name" 
                  value={duplicateForm.name} 
                  onChange={(e) => setDuplicateForm({ ...duplicateForm, name: e.target.value })} 
                />
              </Flex>
              {/* Purpose input field */}
              <Flex direction="column" gap="1">
                <Text size="2" weight="bold">Purpose</Text>
                <Select.Root value={duplicateForm.purpose} onValueChange={(val) => setDuplicateForm({ ...duplicateForm, purpose: val })}>
                  <Select.Trigger style={{ width: '100%' }} />
                  <Select.Content>
                    <Select.Item value="welcome">Welcome email</Select.Item>
                    <Select.Item value="newsletter">Newsletter</Select.Item>
                    <Select.Item value="review">Review request email</Select.Item>
                    <Select.Item value="promotion">Promotional email</Select.Item>
                  </Select.Content>
                </Select.Root>
              </Flex>
              {/* Subject input field */}
              <Flex direction="column" gap="1">
                <Text size="2" weight="bold">Subject</Text>
                <TextField.Root
                  placeholder="Email's subject line" 
                  value={duplicateForm.subject} 
                  onChange={(e) => setDuplicateForm({ ...duplicateForm, subject: e.target.value })} 
                />
              </Flex>
            </Flex>
            <Flex gap="3" mt="5" justify="end">
              <Dialog.Close>
                <Button variant="soft" color="gray" disabled={isDuplicating}>Cancel</Button>
              </Dialog.Close>
              <Button 
                onClick={handleConfirmDuplicate} 
                disabled={!duplicateForm.name.trim() || !duplicateForm.subject.trim() || isDuplicating}
                loading={isDuplicating}
              >
                Create copy
              </Button>
            </Flex>
          </Dialog.Content>
        </Dialog.Root>

        {/* Delete Confirmation Dialog */}
        <AlertDialog.Root open={!!templateToDelete} onOpenChange={(open) => !open && setTemplateToDelete(null)}>
          <AlertDialog.Content style={{ maxWidth: 500 }}>
            <AlertDialog.Title>Delete Template</AlertDialog.Title>
            <AlertDialog.Description size="2">
              Are you sure you want to delete "{templateToDelete?.name}"?
            </AlertDialog.Description>
            
            {isLoadingAutomations ? (
              <Text size="2" color="gray" mt="3">Checking for associated automations...</Text>
            ) : associatedAutomations.length > 0 ? (
              <Callout.Root color="red" mt="3">
                <Callout.Icon>
                  <ExclamationTriangleIcon />
                </Callout.Icon>
                <Callout.Text>
                  <Text weight="medium">
                    {associatedAutomations.length} automation{associatedAutomations.length > 1 ? 's' : ''} will be deleted:
                  </Text>
                  <Flex direction="column" gap="1" mt="2">
                    {associatedAutomations.map((automation) => (
                      <Text key={automation.automation_id} size="1">
                        • {automation.name} {automation.is_active ? '(Active)' : '(Inactive)'}
                      </Text>
                    ))}
                  </Flex>
                </Callout.Text>
              </Callout.Root>
            ) : (
              <Text size="2" color="gray" mt="3">
                No automations are using this template.
              </Text>
            )}

            <Flex gap="3" mt="5" justify="end">
              <AlertDialog.Cancel>
                <Button variant="soft" color="gray">
                  Cancel
                </Button>
              </AlertDialog.Cancel>
              <AlertDialog.Action>
                <Button 
                  variant="solid" 
                  color="red" 
                  onClick={handleDeleteTemplate} 
                  disabled={isDeleting || isLoadingAutomations} 
                  loading={isDeleting}
                >
                  {associatedAutomations.length > 0 
                    ? `Delete template & ${associatedAutomations.length} automation${associatedAutomations.length > 1 ? 's' : ''}`
                    : 'Delete template'
                  }
                </Button>
              </AlertDialog.Action>
            </Flex>
          </AlertDialog.Content>
        </AlertDialog.Root>

        <AlertDialog.Root open={!!whatsappTemplateToDelete} onOpenChange={(open) => !open && setWhatsappTemplateToDelete(null)}>
          <AlertDialog.Content style={{ maxWidth: 450 }}>
            <AlertDialog.Title>Delete WhatsApp template?</AlertDialog.Title>
            <AlertDialog.Description size="2">
              This removes "{whatsappTemplateToDelete?.name}" from the WhatsApp templates list.
            </AlertDialog.Description>

            <Flex gap="3" mt="4" justify="end">
              <AlertDialog.Cancel>
                <Button variant="soft" color="gray">
                  Cancel
                </Button>
              </AlertDialog.Cancel>
              <AlertDialog.Action>
                <Button color="red" onClick={handleDeleteWhatsappTemplate} disabled={isDeletingWhatsappTemplate} loading={isDeletingWhatsappTemplate}>
                  Delete
                </Button>
              </AlertDialog.Action>
            </Flex>
          </AlertDialog.Content>
        </AlertDialog.Root>
      </Flex>
    </>
  );
}
